import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';

import { RingChartComponent } from '../charts/ring-chart.component';
import { ReportChromeService } from '../report-chrome.service';
import { ReportFmtPipe, ReportTPipe } from '../report-i18n.pipe';
import { ReportI18nService } from '../report-i18n.service';
import { ReportRevealDirective } from '../report-reveal.directive';
import { ReportLevel, SubjectReportView } from '../report.model';

/** Nom du plan associé à un palier — voir la note de `subject-levels.section.ts`. */
const PLAN_KEY: Record<ReportLevel, string> = {
  weak: 'remedial',
  average: 'improvement',
  good: 'development',
  excellent: 'gifted',
};

/**
 * Variante de pastille par palier.
 *
 * Le rouge et le vert restent aux extrêmes ; les deux paliers médians prennent
 * l'ambre et le bleu, comme la palette des graphiques. Reprise telle quelle de
 * la maquette, où le tableau des matières emploie déjà ce même appariement.
 */
const BADGE_VARIANT: Record<ReportLevel, string> = {
  weak: 'danger',
  average: 'warning',
  good: 'info',
  excellent: 'success',
};

/**
 * Section « compétences non acquises » : l'anneau et sa lecture.
 *
 * **L'anneau n'est pas un camembert.** Tous les secteurs ont le même angle :
 * il ne compare pas des grandeurs, il donne une identité de couleur à chaque
 * lacune — la même que reprennent la légende, le plan hebdomadaire et les
 * puces des semaines.
 *
 * Le bloc central `.ring__center` est un **frère** du graphique dans
 * `.ring-wrap`, et non son enfant : dans le document d'origine le moteur
 * réécrivait l'`innerHTML` de l'hôte à chaque redessin, ce qui l'aurait
 * effacé. Le composant Angular ne rend que le `<svg>` ; on conserve la
 * structure, c'est elle qui porte le positionnement absolu du libellé.
 *
 * L'index actif est tenu ici plutôt que dans le graphique : la légende et
 * l'anneau mettent en avant le même secteur, dans les deux sens.
 */
@Component({
  selector: 's2e-report-ring',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReportFmtPipe, ReportTPipe, ReportRevealDirective, RingChartComponent],
  templateUrl: './subject-ring.section.html',
  host: { '[style.display]': "'contents'" },
})
export class SubjectRingSection {
  private readonly i18n = inject(ReportI18nService);
  protected readonly chrome = inject(ReportChromeService);

  readonly subject = input.required<SubjectReportView>();

  /** Secteur mis en avant ; `-1` quand rien n'est survolé. */
  protected readonly activeIndex = signal(-1);

  protected readonly gaps = computed(() => this.subject().gaps);

  protected readonly codeDir = computed(() => this.i18n.codeDirection());

  protected readonly ringAria = computed(() =>
    this.i18n.t('ring.aria', { count: this.subject().charts.ring.length }),
  );
  protected readonly statusLabel = computed(() => this.i18n.t('skillTable.status'));
  protected readonly statusValue = computed(() => this.i18n.t('competency.notAcquired'));

  /**
   * Intitulé du palier atteint.
   *
   * Celui de l'API d'abord : les paliers sont paramétrables et leur nom est
   * déjà localisé. Les quatre clés `levels.*` ne servent que de repli, quand le
   * score ne tombe dans aucune tranche du catalogue.
   */
  protected readonly levelLabel = computed(() => {
    const score = this.subject().score;
    if (score.band) return score.band.label;
    return score.level ? this.i18n.t(`levels.${score.level}`) : '';
  });

  protected readonly badgeVariant = computed(() => {
    const level = this.subject().score.level;
    return level ? BADGE_VARIANT[level] : 'neutral';
  });

  /**
   * Texte de classement, rendu en HTML.
   *
   * La chaîne contient un `<strong>` : elle vient du bundle de traductions
   * livré avec l'application, pas d'une saisie utilisateur. Seules les deux
   * variables sont extérieures — l'intitulé du palier et le nom de la matière
   * — et Angular les insère dans une valeur qu'il assainit.
   */
  protected readonly classification = computed(() =>
    this.i18n.t('overview.classification', {
      level: this.levelLabel(),
      subject: this.subject().subjectName,
    }),
  );

  /**
   * Pastille du plan.
   *
   * `overview.planBadge` et non `levelScale.plans` : la maquette formule cette
   * pastille pour cette carte seule, et les deux doivent pouvoir diverger.
   */
  protected readonly planBadge = computed(() => {
    const level = this.subject().score.level;
    return level ? this.i18n.t(`overview.planBadge.${PLAN_KEY[level]}`) : '';
  });

  protected readonly planDescription = computed(() => {
    const level = this.subject().score.level;
    return level ? this.i18n.t(`levelScale.planDescriptions.${PLAN_KEY[level]}`) : '';
  });

  /** Survol et focus mènent au même état : la légende se parcourt au clavier. */
  protected activate(index: number): void {
    this.activeIndex.set(index);
  }

  protected clear(): void {
    this.activeIndex.set(-1);
  }
}
